/**
 * Cliente dos perfis de papel.
 *
 * Os conjuntos abaixo são a cópia do frontend de `app/domain/profiles/catalog.py`,
 * e a ordem importa: é a ordem em que as opções aparecem na tela, e a mesma que
 * o servidor devolve depois de gravar. Manter as duas iguais evita que a lista
 * pule de posição entre o que a pessoa marcou e o que ela vê ao voltar.
 *
 * O rótulo não vem daqui — vem de `i18n/locales/<idioma>/profiles.json`. Aqui
 * ficam só as chaves.
 */

import { api } from '@/lib/apiClient';

export const MODALITIES = [
  'strength',
  'running_walking',
  'functional',
  'mobility',
  'swimming',
] as const;

export const SERVICE_MODES = ['in_person', 'online', 'hybrid'] as const;

export const EXPERIENCE_LEVELS = ['beginner', 'intermediate', 'advanced'] as const;

export const GOALS = [
  'general_health',
  'weight_loss',
  'muscle_gain',
  'endurance',
  'mobility',
  'performance',
] as const;

export const LOCATIONS = ['home', 'gym', 'outdoor', 'studio', 'pool'] as const;

export const EQUIPMENT = [
  'none',
  'dumbbells',
  'barbell',
  'kettlebell',
  'bands',
  'bench',
  'pullup_bar',
  'machines',
  'cardio_machine',
] as const;

export const SESSION_MINUTES = [15, 20, 30, 45, 60, 75, 90, 120] as const;

export type Modality = (typeof MODALITIES)[number];
export type ServiceMode = (typeof SERVICE_MODES)[number];
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number];
export type Goal = (typeof GOALS)[number];
export type TrainingLocation = (typeof LOCATIONS)[number];
export type Equipment = (typeof EQUIPMENT)[number];

export interface StudentProfile {
  exists: boolean;
  goals: Goal[];
  experience_level: ExperienceLevel;
  available_minutes: number[];
  locations: TrainingLocation[];
  equipment: Equipment[];
  modalities: Modality[];
  restrictions_text: string | null;
}

export type StudentProfilePayload = Omit<StudentProfile, 'exists'>;

export interface ProfessionalProfile {
  slug: string;
  bio: string | null;
  content_locale: string;
  modalities: Modality[];
  service_modes: ServiceMode[];
  country_code: string | null;
  city: string | null;
  neighborhood: string | null;
  registration_text: string | null;
  /** Definido pela administração. A tela mostra, nunca envia. */
  verification_status: 'unverified' | 'pending' | 'verified' | 'suspended';
  is_public: boolean;
  /** `false` enquanto publicado: trocar o endereço quebraria links divulgados. */
  can_change_slug: boolean;
}

export type ProfessionalProfilePayload = Omit<
  ProfessionalProfile,
  'verification_status' | 'can_change_slug'
>;

/**
 * A vitrine que qualquer pessoa abre.
 *
 * Repare no que **não** tem: identificador, bairro e situação de verificação.
 * A view do banco não devolve esses campos, e a decisão de cada ausência está
 * no cabeçalho da migration `0005`.
 */
export interface PublicProfessional {
  slug: string;
  display_name: string;
  bio: string | null;
  content_locale: string;
  modalities: Modality[];
  service_modes: ServiceMode[];
  country_code: string | null;
  city: string | null;
  /** Informado pelo profissional. O Movhuman não confere junto a conselho. */
  registration_text: string | null;
}

/** Sem sessão. Endereço inexistente, rascunho e suspenso devolvem 404 iguais. */
export const getPublicProfessional = (slug: string) =>
  api.get<PublicProfessional>(`/public/professionals/${encodeURIComponent(slug)}`);

export const getStudentProfile = () => api.get<StudentProfile>('/me/student-profile');

export const saveStudentProfile = (payload: StudentProfilePayload) =>
  api.put<StudentProfile>('/me/student-profile', payload);

/** `null` quando ainda não existe — é formulário em branco, não erro. */
export const getProfessionalProfile = () =>
  api.get<ProfessionalProfile | null>('/me/professional-profile');

export const saveProfessionalProfile = (payload: ProfessionalProfilePayload) =>
  api.put<ProfessionalProfile>('/me/professional-profile', payload);
